import React from "react";
import { Button, Popconfirm, Tag, Tooltip } from "antd";
import { Pencil, Power } from "lucide-react";
import { ISubAdminPayload } from "../../../types/api/subAdmin";

interface GetSubAdminColumnsParams {
  onEdit: (record: ISubAdminPayload) => void;
  onToggleStatus: (record: ISubAdminPayload) => void;
}

export function getSubAdminColumns({
  onEdit,
  onToggleStatus,
}: GetSubAdminColumnsParams) {
  return [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text: string) => (
        <div className="font-medium text-slate-800">{text}</div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email: string) => (
        <div className="text-slate-700 text-xs">{email}</div>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (phone: string) => (
        <div className="text-slate-600 text-xs">{phone || "-"}</div>
      ),
    },
    {
      title: "Type",
      dataIndex: "user_type",
      key: "user_type",
      width: 110,
      render: (userType: string) => (
        <Tag color={userType === "SUPER_ADMIN" ? "gold" : "default"}>
          {userType === "SUPER_ADMIN" ? "Super Admin" : "Sub Admin"}
        </Tag>
      ),
    },
    {
      title: "Permissions",
      dataIndex: "permissions",
      key: "permissions",
      render: (permissions: { module: string; action: string }[], record: ISubAdminPayload) => {
        // SUPER_ADMIN always bypasses the permissions array entirely (see
        // requirePermission middleware, api-side) - it's never populated
        // for a super admin, since it's meaningless for them. Previously
        // that showed as "No access granted" here, which read as if their
        // access had been revoked rather than being unrestricted (Oliver,
        // Sept 2026: "shows 'No access granted' under Permissions for all
        // of us, although we're Super Admins with full access").
        if (record.user_type === "SUPER_ADMIN") {
          return <Tag color="gold">Full access</Tag>;
        }
        const modules = Array.from(new Set((permissions || []).map((p) => p.module)));
        if (!modules.length) return <span className="text-slate-400 italic text-xs">No access granted</span>;
        return (
          <div className="flex flex-wrap gap-1 max-w-xs">
            {modules.map((m) => (
              <Tag key={m}>{m}</Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: boolean) => (
        <Tag color={status ? "green" : "red"}>
          {status ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 90,
      render: (_: any, record: ISubAdminPayload) => (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<Pencil size={14} />}
              onClick={() => onEdit(record)}
              className="text-slate-500 hover:text-blue-600 border-none shadow-none bg-transparent"
            />
          </Tooltip>
          <Tooltip title={record.status ? "Deactivate" : "Activate"}>
            <Popconfirm
              title={record.status ? "Deactivate this admin account?" : "Reactivate this admin account?"}
              description={record.status ? "They will no longer be able to log in." : undefined}
              onConfirm={() => onToggleStatus(record)}
              okText={record.status ? "Deactivate" : "Activate"}
              okType={record.status ? "danger" : "primary"}
              cancelText="Cancel"
            >
              <Button
                size="small"
                icon={<Power size={14} />}
                className="text-slate-500 hover:text-red-600 border-none shadow-none bg-transparent"
              />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];
}
